// ----------------------------------------------------------------------

/**
 * 오류 → 메시지 문자열 정규화의 단일 진입점.
 *
 * ⚠️ `err instanceof Error ? err.message : String(err)` 관용구와 **글자 그대로 등가**여야 한다 —
 * 그 관용구 자리들이 이 함수로 기계 치환됐고, 뽑힌 문자열은 로그·토스트로 사용자에게 보인다.
 * 우선순위를 바꾸면(예: `err?.response?.data?.message` 를 먼저 보게) 치환 자리의 문구가 달라진다.
 *
 * `fallback` 은 뽑힌 메시지가 빈 문자열일 때만 쓴다 — `error.message || '기본 문장'` 그룹의 자리.
 */
export function toErrorMessage(err: unknown, fallback?: string): string {
  const message = err instanceof Error ? err.message : String(err);
  return fallback === undefined ? message : message || fallback;
}

/** 연결이 끊겼거나 응답을 받지 못했을 때 사용자에게 보일 문장 */
export const NETWORK_ERROR_MESSAGE = '연결이 불안정해요. 잠시 뒤 다시 시도해 주세요.';

/**
 * 오류 → **사용자 화면에 띄울 문장**. 서버가 지은 한국어 문장이면 그대로 쓰고, 원문(영어 SDK · 네트워크 · 코드)이면 바꾼다.
 * 🔑 「Failed to fetch」 · 「Network Error」 · 시간 초과 → `NETWORK_ERROR_MESSAGE` · 그 밖의 원문 → `fallback`.
 * 🔑 axios 오류는 서버가 실어 보낸 `response.data.error`·`message` 를 먼저 본다.
 * @param err `catch` 가 준 것 · 서버가 준 오류 문장(문자열)
 * @param fallback 이 화면의 문장 — 「저장하지 못했어요. 잠시 뒤 다시 시도해 주세요.」처럼 무엇이 안 됐는지
 */
export function toUserErrorMessage(err: unknown, fallback: string): string {
  if (isNetworkError(err)) return NETWORK_ERROR_MESSAGE;
  const candidates = [readServerMessage(err), typeof err === 'string' ? err : readMessage(err)];
  return candidates.find((text) => isUserFacingMessage(text)) ?? fallback;
}

/**
 * 사용자에게 그대로 보여도 되는 문장인가 — 한글이 있고, 개발 용어(영어 낱말)가 섞이지 않았다.
 * 🔑 화면에 쓰는 서비스 · 플랫폼 이름(Instagram · Excel …)과 세 글자 이하 약어(MCP · URL)는 괜찮다.
 */
export function isUserFacingMessage(text: unknown): text is string {
  if (typeof text !== 'string' || !/[가-힣]/.test(text)) return false;
  const words = text.match(/[A-Za-z][A-Za-z_.-]{3,}/g) ?? [];
  return words.every((word) => USER_FACING_WORDS.has(word.toLowerCase()));
}

/** 연결이 끊겼거나 응답이 오지 않았다 — 브라우저 fetch · axios 네트워크 · 시간 초과 */
export function isNetworkError(err: unknown): boolean {
  const message = readMessage(err);
  const code = String((err as { code?: unknown } | null)?.code ?? '');
  if (['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET'].includes(code)) return true;
  return /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?|Network Error|network error)$/i.test(message) || /^timeout of \d+ms exceeded$/.test(message);
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 사용자 문장에 섞여도 되는 영어 낱말(소문자) */
const USER_FACING_WORDS = new Set([
  'instagram', 'tiktok', 'youtube', 'google', 'naver', 'kakao', 'meta', 'facebook', 'threads', 'reddit', 'twitter', 'slack',
  'excel', 'xlsx', 'csv', 'catchmoments', 'research', 'discover', 'lite', 'growth', 'brand', 'enterprise', 'individual', 'trial',
  'claude', 'chatgpt', 'email', 'invoice', 'link', 'shorts', 'reels', 'story',
]);

function readMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : '';
}

function readServerMessage(err: unknown): string {
  const data = (err as { response?: { data?: { error?: unknown; message?: unknown } } } | null)?.response?.data;
  const text = data?.error ?? data?.message;
  return typeof text === 'string' ? text : '';
}
